import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import OfferCard from "@/components/OfferCard";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { fetchOffersBySellerId } from "@/lib/api/offers";
import { Link } from "react-router-dom";
import { getSupabaseClient } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProfileReviews from "@/components/ProfileReviews";
import { getSafeClient } from "@/integrations/supabase/safeClient";
import { getAvatarUrl, generateAvatarUrl } from "@/lib/utils/avatars";

const Profile = () => {
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [bio, setBio] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = user?.id;

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  const { data: userData } = useQuery({
    queryKey: ['profile', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const supabase = await getSupabaseClient();
      const { data, error } = await supabase
        .from('profiles')
        .select(`id, name, avatar_url, bio, created_at`)
        .eq('id', userId)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
      return data;
    },
    enabled: !!userId,
  });

  useEffect(() => {
    if (userData) {
      setName(userData.name || "");
      setAvatarUrl(userData.avatar_url || generateAvatarUrl(userData.name || ""));
      setBio(userData.bio || "");
    }
  }, [userData]);

  const updateProfileMutation = useMutation({
    mutationFn: async (values: { name: string; avatar_url: string; bio: string }) => {
      if (!userId) throw new Error("User not logged in");
      
      const supabase = await getSupabaseClient();
      const { error } = await supabase
        .from('profiles')
        .update({
          name: values.name,
          avatar_url: values.avatar_url,
          bio: values.bio
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Profile updated!",
        description: "Your profile has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      setIsEditMode(false);
    },
    onError: (error) => {
      console.error("Error updating profile:", error);
      toast({
        title: "Error updating profile",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    updateProfileMutation.mutate({ name, avatar_url: avatarUrl, bio });
  };

  const handleSignOut = async () => {
    const supabase = await getSupabaseClient();
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error signing out",
        description: "Please try again.",
        variant: "destructive",
      });
    } else {
      navigate("/login");
    }
  };

  const { data: offers, isLoading: isOffersLoading, error: offersError } = useQuery({
    queryKey: ['sellerOffers', userId],
    queryFn: () => fetchOffersBySellerId(userId || ''),
    enabled: !!userId,
  });

  const categoryMap: Record<string, number> = {};
  offers?.forEach(offer => {
    const category = offer.tags && offer.tags.length > 0 ? offer.tags[0] : offer.link_type || "Other";
    if (!categoryMap[category]) {
      categoryMap[category] = 0;
    }
    categoryMap[category]++;
  });

  const categoryList = Object.keys(categoryMap).sort((a, b) => categoryMap[b] - categoryMap[a]);

  const rawOffers = queryClient.getQueryData(['sellerOffers', userId]) as any[] || [];

  const fetchProfileReviews = async (profileId: string) => {
    try {
      const supabaseClient = await getSafeClient();
      const { data, error } = await supabaseClient.rpc('get_profile_reviews', { profile_id_param: profileId });
      if (error) {
        throw error;
      }
      return data;
    } catch (error) {
      console.error("Error fetching profile reviews:", error);
      throw error;
    }
  };

  const handleAvatarUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAvatarUrl(value || generateAvatarUrl(name));
  };

  return (
    <div className="container mx-auto py-10">
      <div className="grid grid-cols-1 gap-6">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="offers">Your Offers</TabsTrigger>
            <TabsTrigger value="reviews">Your Reviews</TabsTrigger>
          </TabsList>
          
          <TabsContent value="profile" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Your Profile</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center mb-4">
                    <Avatar className="w-24 h-24">
                      <AvatarImage src={avatarUrl} alt={name} />
                      <AvatarFallback>{name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => setIsEditMode(!isEditMode)}
                    >
                      {isEditMode ? "Cancel Edit" : "Edit Profile"}
                    </Button>
                  </div>
                  <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={!isEditMode}
                      />
                    </div>
                    <div>
                      <Label htmlFor="avatarUrl">Avatar URL</Label>
                      <Input
                        type="url"
                        id="avatarUrl"
                        value={avatarUrl}
                        onChange={handleAvatarUrlChange}
                        disabled={!isEditMode}
                        placeholder={generateAvatarUrl(name)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        disabled={!isEditMode}
                        placeholder="No bio available"
                      />
                    </div>
                    {isEditMode && (
                      <Button type="submit" disabled={updateProfileMutation.isPending}>
                        {updateProfileMutation.isPending ? (
                          <>
                            Updating <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                          </>
                        ) : (
                          "Update Profile"
                        )}
                      </Button>
                    )}
                  </form>
                  <Separator className="my-4" />
                  <Button variant="destructive" onClick={handleSignOut}>
                    Sign Out
                  </Button>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <ProfileReviews 
                  profileId={userId || ""} 
                  profileName={name || "User"}
                />
                
                <Card>
                  <CardHeader>
                    <CardTitle>Seller Settings</CardTitle>
                    <CardDescription>Manage your payments and seller profile</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Setup your Stripe Connect account to receive payments for your services.
                      </p>
                      <Button asChild>
                        <Link to="/settings/seller">Manage Seller Settings</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="offers" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Offers</CardTitle>
              </CardHeader>
              <CardContent>
                {isOffersLoading ? (
                  <div className="flex justify-center">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : offersError ? (
                  <p className="text-red-500">Error loading offers.</p>
                ) : (
                  <>
                    {offers && offers.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {offers.map((offer) => (
                          <OfferCard 
                            key={offer.id}
                            id={offer.id}
                            title={offer.title}
                            price={offer.price_cents / 100}
                            dr={offer.dr}
                            da={offer.da}
                            traffic={offer.traffic_estimate}
                            linkType={offer.link_type}
                            turnaroundDays={offer.turnaround_days}
                            tags={offer.tags || []}
                            sellerName={offer.seller_profile?.name || "Unknown"}
                            sellerId={userId || ""}
                            sellerRating={4.5}  // Default or calculate actual rating
                            sellerReviewCount={10} // Default or use actual count
                          />
                        ))}
                      </div>
                    ) : (
                      <p>No offers created yet.</p>
                    )}
                    <Button variant="secondary" className="mt-4">
                      <Link to="/create-offer">Create New Offer</Link>
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="reviews" className="mt-6">
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Reviews</CardTitle>
                  <CardDescription>Reviews you've received for your services as a buyer or seller</CardDescription>
                </CardHeader>
                <CardContent>
                  <ProfileReviews 
                    profileId={userId || ""} 
                    reviewType="profile"
                  />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Marketplace Offer Reviews</CardTitle>
                  <CardDescription>Reviews for your backlink offers in the marketplace</CardDescription>
                </CardHeader>
                <CardContent>
                  <ProfileReviews 
                    profileId={userId || ""} 
                    reviewType="offer"
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
